import React from "react";
import { AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import { ItemWrap } from "./ItemWrap";
import { SignalEditor } from "./signals";
import { OrGroups } from "../types/flowchart";
import { ConfiguredSignal } from "../../../../config/signals";
import type { CopiedSignalPayload } from "./signals/types";
import styles from "./OrGroupEditor.module.css";

const plusBtn = "plus-btn";

interface OrGroupEditorProps {
  groups: OrGroups;
  legId: string;
  label: string;
  category: "entry" | "exit";
  onAddSignal: (legId: string, groupIdx: number) => void;
  onRemoveSignal: (legId: string, groupIdx: number, signalIdx: number) => void;
  onUpdateSignal: (legId: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) => void;
  onAddGroup: (legId: string) => void;
  onRemoveGroup: (legId: string, groupIdx: number) => void;
  allowedSignalTypes?: string[];
  copiedSignal: CopiedSignalPayload | null;
  onCopySignal: (signal: CopiedSignalPayload) => void;
}

export const OrGroupEditor: React.FC<OrGroupEditorProps> = ({
  groups,
  legId,
  label,
  category,
  onAddSignal,
  onRemoveSignal,
  onUpdateSignal,
  onAddGroup,
  onRemoveGroup,
  allowedSignalTypes,
  copiedSignal,
  onCopySignal,
}) => {
  const hasSignals = groups.length > 0;

  return (
    <div className={styles.container}>
      <label className={styles.label}>{label}</label>
      <div className={styles.groupsContainer}>
        {/* Show just the + button when no signals */}
        {!hasSignals && (
          <button
            className={plusBtn}
            onClick={() => onAddGroup(legId)}
            aria-label="Add signal"
          >
            <Plus className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Show signal groups when there are signals */}
        {hasSignals && (
          <>
            <AnimatePresence initial={false}>
              {groups.map((andGroup, groupIdx) => (
                <ItemWrap key={`group-${legId}-${groupIdx}`}>
                  <div className={styles.andGroup}>
                    {/* Signals within this AndGroup */}
                    <div className={styles.signalsContainer}>
                      <AnimatePresence initial={false}>
                        {andGroup.map((signal, signalIdx) => (
                          <ItemWrap key={signal.id}>
                            <SignalEditor
                              signal={signal}
                              category={category}
                              onUpdate={(updated) => onUpdateSignal(legId, groupIdx, signalIdx, updated)}
                              onRemove={() => onRemoveSignal(legId, groupIdx, signalIdx)}
                              allowedSignalTypes={allowedSignalTypes}
                              copiedSignal={copiedSignal}
                              onCopySignal={onCopySignal}
                            />
                          </ItemWrap>
                        ))}
                      </AnimatePresence>
                      <button
                        className={plusBtn}
                        onClick={() => onAddSignal(legId, groupIdx)}
                        aria-label="Add signal"
                      >
                        <Plus className="h-2.5 w-2.5" />
                      </button>
                    </div>

                    {/* OR Divider - show after each group except the last */}
                    {groupIdx < groups.length - 1 && (
                      <div className={styles.orDivider}>
                        <div className={styles.orLine} />
                        <span className={styles.orText}>OR</span>
                        <div className={styles.orLine} />
                        <button
                          className={styles.orRemoveButton}
                          onClick={() => onRemoveGroup(legId, groupIdx + 1)}
                          aria-label="Remove OR group"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </ItemWrap>
              ))}
            </AnimatePresence>

            {/* Add OR Group button */}
            <button
              className={styles.addOrButton}
              onClick={() => onAddGroup(legId)}
              aria-label="Add OR group"
            >
              <Plus className="h-2.5 w-2.5" />
              <span>OR</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
